"use client";

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  memo,
  createContext,
  useContext,
} from "react";
import { createPortal } from "react-dom";
import ConfigSettings from "./ConfigSettings";
import LayoutSettings from "./LayoutSettings";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faCog,
  faPaintBrush,
} from "@fortawesome/free-solid-svg-icons";

// Create context for action buttons
const WidgetSettingsContext = createContext({
  setActionButton: () => {},
  clearActionButton: () => {},
});

export const useWidgetSettings = () => useContext(WidgetSettingsContext);

// Memoize the settings components
const MemoizedConfigSettings = memo(ConfigSettings);

// Constants for positioning
const MENU_WIDTH = 300;

const WidgetSettings = memo(function WidgetSettings({
  widget,
  config,
  layout,
  settingsType,
  onLayoutChange,
  onConfigChange,
  onClose,
  previewMode,
  totalColumns,
  onLockToggle,
  spaceData,
  allWidgets,
}) {
  const panelRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const [actionButton, setActionButton] = useState(null);

  // Context value for child components
  const contextValue = {
    setActionButton: useCallback((button) => {
      setActionButton(button);
    }, []),
    clearActionButton: useCallback(() => {
      setActionButton(null);
    }, []),
  };

  // Clear action button when settings type changes
  useEffect(() => {
    setActionButton(null);
  }, [settingsType, widget.id]);

  // Create a wrapper for config updates to ensure they're properly handled
  const handleConfigUpdate = useCallback(
    (widgetId, updates) => {
      console.log("WidgetSettings config update:", widgetId, updates);

      // Check if this is a batch update with multiple widgets
      if (
        updates &&
        updates.type === "CONFIG_CHANGE_BATCH" &&
        updates.widgets
      ) {
        onConfigChange(null, updates);
        return;
      }

      // Get the config from the updates
      if (updates && updates.config) {
        onConfigChange(updates.config);
      } else {
        // If unexpected update format, forward as is
        onConfigChange(updates);
      }
    },
    [onConfigChange]
  );

  // Create a wrapper for layout updates to ensure they're properly handled
  const handleLayoutUpdate = useCallback(
    (widgetId, updates) => {
      console.log("WidgetSettings layout update:", widgetId, updates);

      // Check if this is a preview update - handle differently
      if (updates && updates.isPreview) {
        console.log(
          "🎨 [Preview] Layout preview update - updating widget state only"
        );
        // For preview updates, still call onLayoutChange but with special flags
        if (updates.layout) {
          onLayoutChange(updates.layout, {
            isPreview: true,
            skipDatabase: true,
            skipHistory: true,
            triggerAutosave: false,
          });
        }
        return;
      }

      // Check if this is a batch update with multiple widgets
      if (
        updates &&
        updates.type === "LAYOUT_CHANGE_BATCH" &&
        updates.widgets
      ) {
        onLayoutChange(null, updates);
        return;
      }

      // Get the layout from the updates
      if (updates && updates.layout) {
        // Pass through the isRealTimeUpdate flag if present
        if (updates.isRealTimeUpdate) {
          onLayoutChange(updates.layout, { isRealTimeUpdate: true });
        } else {
          onLayoutChange(updates.layout);
        }
      } else {
        // If unexpected update format, forward as is
        onLayoutChange(updates);
      }
    },
    [onLayoutChange]
  );

  // Handle close with animation
  const handleClose = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      setIsVisible(false);
      setTimeout(() => {
        onClose();
      }, 200);
    },
    [onClose]
  );

  // Store the last known widget position to detect changes
  const lastWidgetPosition = useRef(null);

  // Brand new positioning system - keep it simple and reliable
  const calculatePosition = useCallback(
    (forceUpdate = false) => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const menuHeight = 400; // Fixed height for consistency
      const gap = 10; // Gap between widget and panel

      // Find the widget element
      let widgetEl = document.querySelector(`[data-widget-id="${widget.id}"]`);
      if (!widgetEl) {
        console.log(
          `[Settings Position] Widget element not found for ID: ${widget.id}`
        );
        // Fallback position
        setMenuPosition({
          left: 20,
          top: 20,
          placement: "fallback",
        });
        return;
      }

      const rect = widgetEl.getBoundingClientRect();

      // Check if widget position has actually changed
      const currentPosition = {
        left: rect.left,
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
      };

      // Only update if position has meaningfully changed (more than 3px) or forced
      if (lastWidgetPosition.current && !forceUpdate) {
        const deltaX = Math.abs(
          currentPosition.left - lastWidgetPosition.current.left
        );
        const deltaY = Math.abs(
          currentPosition.top - lastWidgetPosition.current.top
        );

        if (deltaX < 3 && deltaY < 3) {
          return; // Position hasn't changed significantly
        }
      }

      console.log(`[Settings Position] Widget moved to:`, currentPosition);

      // Store the current position
      lastWidgetPosition.current = currentPosition;

      // Strategy 1: Try to the right
      if (rect.right + gap + MENU_WIDTH < viewportWidth - 20) {
        setMenuPosition({
          left: rect.right + gap,
          top: Math.max(
            20,
            Math.min(rect.top, viewportHeight - menuHeight - 20)
          ),
          placement: "right",
        });
        return;
      }

      // Strategy 2: Try to the left
      if (rect.left - gap - MENU_WIDTH > 20) {
        setMenuPosition({
          left: rect.left - gap - MENU_WIDTH,
          top: Math.max(
            20,
            Math.min(rect.top, viewportHeight - menuHeight - 20)
          ),
          placement: "left",
        });
        return;
      }

      // Strategy 3: Try below
      if (rect.bottom + gap + menuHeight < viewportHeight - 20) {
        setMenuPosition({
          left: Math.max(
            20,
            Math.min(rect.left, viewportWidth - MENU_WIDTH - 20)
          ),
          top: rect.bottom + gap,
          placement: "below",
        });
        return;
      }

      // Strategy 4: Try above
      if (rect.top - gap - menuHeight > 20) {
        setMenuPosition({
          left: Math.max(
            20,
            Math.min(rect.left, viewportWidth - MENU_WIDTH - 20)
          ),
          top: rect.top - gap - menuHeight,
          placement: "above",
        });
        return;
      }

      // Fallback: Top right corner
      setMenuPosition({
        left: viewportWidth - MENU_WIDTH - 20,
        top: 20,
        placement: "corner",
      });
    },
    [widget.id]
  );

  // Position the menu when component mounts and on window resize
  useEffect(() => {
    setMounted(true);

    // Add a small delay to ensure the widget is fully rendered
    const positionTimeout = setTimeout(() => {
      calculatePosition(true); // Force initial calculation
    }, 100);

    // Show with animation and recalculate position once more
    const showTimeout = setTimeout(() => {
      setIsVisible(true);
      // Recalculate position once the panel is visible in case anything changed
      setTimeout(() => calculatePosition(true), 50); // Force recalculation
    }, 150);

    // Recalculate on resize or scroll
    window.addEventListener("resize", calculatePosition);
    window.addEventListener("scroll", calculatePosition, true);

    // Throttled position monitoring using RAF
    let rafId = null;
    let isMonitoring = true;
    let lastCheckTime = 0;
    const CHECK_INTERVAL = 100; // Check every 100ms

    const throttledPositionCheck = (currentTime) => {
      if (isMonitoring && mounted) {
        // Only check position every CHECK_INTERVAL milliseconds
        if (currentTime - lastCheckTime >= CHECK_INTERVAL) {
          calculatePosition();
          lastCheckTime = currentTime;
        }
        rafId = requestAnimationFrame(throttledPositionCheck);
      }
    };

    // Start throttled monitoring
    rafId = requestAnimationFrame(throttledPositionCheck);

    // Listen for mouse events to detect dragging operations
    let isDragging = false;
    let dragInterval = null;

    const handleMouseDown = (e) => {
      // Check if this is a drag operation on our widget
      const targetWidget = e.target.closest("[data-widget-id]");
      if (targetWidget && targetWidget.dataset.widgetId === widget.id) {
        isDragging = true;
        console.log(`[Settings Position] Drag started for widget ${widget.id}`);
        // Force immediate position update when drag starts
        setTimeout(() => {
          calculatePosition(true);
        }, 0);
      }
    };

    const handleMouseUp = () => {
      if (isDragging) {
        isDragging = false;
        console.log(`[Settings Position] Drag ended for widget ${widget.id}`);
        // Final position calculation after drag ends
        setTimeout(() => {
          calculatePosition(true);
        }, 100);
        // Additional delayed calculation to catch any layout changes
        setTimeout(() => {
          calculatePosition(true);
        }, 300);
      }
    };

    const handleMouseMove = (e) => {
      if (isDragging) {
        // Force immediate position update during drag
        calculatePosition(true);
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("mousemove", handleMouseMove);

    // Set up MutationObserver to watch for widget position changes
    const widgetEl = document.querySelector(`[data-widget-id="${widget.id}"]`);
    let observer = null;
    let dragObserver = null;

    if (widgetEl) {
      observer = new MutationObserver((mutations) => {
        let shouldRecalculate = false;

        mutations.forEach((mutation) => {
          // Check if the widget's style attribute changed (position changes)
          if (
            mutation.type === "attributes" &&
            mutation.attributeName === "style"
          ) {
            shouldRecalculate = true;
          }
          // Check if the widget's position in the DOM changed
          if (mutation.type === "childList") {
            shouldRecalculate = true;
          }
        });

        if (shouldRecalculate) {
          // Use requestAnimationFrame to ensure DOM updates are complete
          requestAnimationFrame(() => {
            calculatePosition();
          });
        }
      });

      // Observe the widget element for attribute changes (style changes)
      observer.observe(widgetEl, {
        attributes: true,
        attributeFilter: ["style"],
        childList: false,
        subtree: false,
      });

      // Also observe the parent container for child list changes (reordering)
      const parentContainer = widgetEl.parentElement;
      if (parentContainer) {
        observer.observe(parentContainer, {
          attributes: false,
          childList: true,
          subtree: false,
        });
      }

      // Set up a more aggressive observer for the masonry container to catch drag operations
      const masonryContainer = document.querySelector(
        "[data-masonry-container]"
      );
      if (masonryContainer) {
        dragObserver = new MutationObserver(() => {
          // Recalculate position whenever the masonry layout changes
          requestAnimationFrame(() => {
            calculatePosition();
          });
        });

        dragObserver.observe(masonryContainer, {
          attributes: true,
          childList: true,
          subtree: true,
          attributeFilter: ["style", "data-grid-x", "data-grid-y"],
        });
      }
    }

    return () => {
      clearTimeout(positionTimeout);
      clearTimeout(showTimeout);
      window.removeEventListener("resize", calculatePosition);
      window.removeEventListener("scroll", calculatePosition, true);

      // Stop continuous monitoring
      isMonitoring = false;
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      // Clean up drag event listeners
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mousemove", handleMouseMove);

      if (observer) {
        observer.disconnect();
      }
      if (dragObserver) {
        dragObserver.disconnect();
      }
    };
  }, [widget.id, calculatePosition]);

  // Force position recalculation when widget ID changes
  useEffect(() => {
    // Reset position tracking when widget changes
    lastWidgetPosition.current = null;
    // Force immediate recalculation
    setTimeout(() => {
      calculatePosition(true);
    }, 50);
  }, [widget.id, calculatePosition]);

  // Handle clicks outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        handleClose();
      }
    };

    const stopPropagation = (event) => {
      event.stopPropagation();
    };

    // Add event listeners
    document.addEventListener("mousedown", handleClickOutside);

    // Stop propagation on the panel itself
    if (panelRef.current) {
      panelRef.current.addEventListener("click", stopPropagation, true);
      panelRef.current.addEventListener("mousedown", stopPropagation, true);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);

      if (panelRef.current) {
        panelRef.current.removeEventListener("click", stopPropagation, true);
        panelRef.current.removeEventListener(
          "mousedown",
          stopPropagation,
          true
        );
      }
    };
  }, [handleClose]);

  // Make queueAction globally available for batch updates
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.queueLayoutAction = (payload, actionType) => {
        console.log("Queue layout action called", actionType);
        if (actionType === "LAYOUT_CHANGE_BATCH" && payload.widgets) {
          const widgetsToUpdate = Object.entries(payload.widgets);
          if (widgetsToUpdate.length > 0) {
            console.log(
              "Queuing batch update with",
              widgetsToUpdate.length,
              "widgets"
            );
          }
        }
      };

      return () => {
        delete window.queueLayoutAction;
      };
    }
  }, []);

  if (!mounted || !menuPosition) return null;

  // Render the settings panel in a portal
  return createPortal(
    <div
      onClick={(e) => {
        // This stops clicks from reaching elements underneath
        e.stopPropagation();
      }}
      className="settings-portal-container"
    >
      {/* Semi-transparent backdrop to capture clicks */}
      <div
        className="fixed inset-0"
        style={{ zIndex: 9998 }}
        onClick={handleClose}
        onMouseDown={(e) => e.stopPropagation()}
      />

      {/* Menu panel - using fixed positioning */}
      <div
        ref={panelRef}
        data-settings-panel
        className={`fixed bg-black rounded-xl shadow-2xl border border-white/10 
          text-white overflow-hidden transition-all duration-200 ease-out w-[300px] max-w-[90vw]
          ${isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}
        style={{
          zIndex: 9999,
          left: `${menuPosition.left}px`,
          top: `${menuPosition.top}px`,
          maxHeight: `${menuPosition.height || Math.min(400, window.innerHeight - menuPosition.top - 40)}px`,
          transform: "none",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={(e) => {
          // Double ensure propagation is stopped
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          // Prevent dragging the widget when interacting with menu
          e.stopPropagation();
        }}
      >
        {/* Fixed Header */}
        <div className="flex-shrink-0 border-b border-white/10 p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon
              icon={settingsType === "config" ? faCog : faPaintBrush}
              className="h-4 w-4 text-white/70"
            />
            <h3 className="text-sm font-medium">
              {settingsType === "config" ? "Configuration" : "Layout & Theme"}
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="text-white/50 hover:text-white p-1 rounded-md hover:bg-white/10 transition-colors"
            aria-label="Close settings"
          >
            <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
          </button>
        </div>

        {/* Scrollable Content Area */}
        <WidgetSettingsContext.Provider value={contextValue}>
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="p-4">
              {settingsType === "config" ? (
                <MemoizedConfigSettings
                  widget={widget}
                  handleWidgetUpdate={handleConfigUpdate}
                />
              ) : (
                <LayoutSettings
                  key={widget.id}
                  widget={widget}
                  handleWidgetUpdate={handleLayoutUpdate}
                  allWidgets={allWidgets}
                />
              )}
            </div>
          </div>

          {/* Fixed Footer for Action Buttons */}
          {actionButton && (
            <div className="flex-shrink-0 border-t border-white/10 bg-black/95 backdrop-blur-sm p-4">
              {actionButton}
            </div>
          )}
        </WidgetSettingsContext.Provider>
      </div>
    </div>,
    document.body
  );
});

WidgetSettings.displayName = "WidgetSettings";

export default WidgetSettings;
